// pages/AddExpenses.tsx
import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DurationSelector } from '../components/DurationSelector'
import Input from '../components/Input'
import SubmitButton from '../components/SubmitButton'
import TagHeader from '../components/TagHeader'
import { TextArea } from '../components/TextArea'
import { useTagContext } from '../contexts/TagContext'
import {
  getAdditionalExpenses,
  tagService,
  updateAdditionalExpenses,
} from '../services/tagService'

const AddExpenses: React.FC = () => {
  const { tagId } = useParams()
  const navigate = useNavigate()
  const { getCurrentTag, refreshTags } = useTagContext()
  const currentTag = getCurrentTag(tagId)
  const isFinal = currentTag?.status === 'SIGNED'

  const [tolls, setTolls] = useState('')
  const [yardTravelTime, setYardTravelTime] = useState('')
  const [homeTravelTime, setHomeTravelTime] = useState('')
  const [notes, setNotes] = useState('')
  const [isNotesModified, setIsNotesModified] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const fetchExpenses = async () => {
      if (tagId) {
        try {
          const expenses = await getAdditionalExpenses(tagId)
          setTolls(expenses.tolls?.toString() || '')
          setYardTravelTime(expenses.yardTime?.toString() || '')
          setHomeTravelTime(expenses.homeTime?.toString() || '')
        } catch (error) {
          console.error('Failed to fetch expenses:', error)
          toast.error('Failed to load existing expenses')
        }
      }
    }

    fetchExpenses()
  }, [tagId])

  const validateNumber = (value: string): boolean => {
    if (!value) return true
    const num = Number.parseFloat(value)
    return !Number.isNaN(num) && num >= 0
  }

  const handleTollsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || validateNumber(value)) {
      setTolls(value)
    }
  }

  const handleNotesChange = (value: string) => {
    setNotes(value)
    setIsNotesModified(true)
  }

  const handleSubmit = async () => {
    if (!tagId) return

    if (!validateNumber(tolls)) {
      toast.error('Please enter a valid amount for tolls')
      return
    }

    setIsSubmitting(true)
    try {
      await updateAdditionalExpenses(tagId, {
        tolls: tolls || '0',
        yardTime: yardTravelTime || '0',
        homeTime: homeTravelTime || '0',
        notes: notes,
      })

      await refreshTags()

      toast.success('Additional expenses updated successfully')
      navigate(`/tags/open/${tagId}/confirmation`)
    } catch (error) {
      console.error('Failed to update expenses:', error)
      toast.error('Failed to update expenses. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={css(styles.container)}>
      <TagHeader
        tagId={tagId}
        title={'Fill Additional Info'}
        status={currentTag?.status || 'IN_PROGRESS'}
        backUrl="/tags/open"
      />
      <div className={css(styles.content)}>
        <div className={css(styles.heading)}>
          <h1 className={css(styles.title)}>Additional Info</h1>
        </div>

        <div className={css(styles.formSection)}>
          <div className={css(styles.inputGroup)}>
            <label className={css(styles.label)}>Tolls</label>
            <div className={css(styles.inputWrapper)}>
              <span className={css(styles.dollarSign)}>$</span>
              <Input
                value={tolls}
                onChange={handleTollsChange}
                type="number"
                step="0.01"
                min="0"
                className={css(styles.tollsInput)}
              />
            </div>
          </div>

          <div className={css(styles.inputGroup)}>
            <label className={css(styles.label)}>
              Travel Time to Pick Up Location
            </label>
            <DurationSelector
              value={yardTravelTime}
              onChange={setYardTravelTime}
            />
          </div>

          <div className={css(styles.inputGroup)}>
            <label className={css(styles.label)}>Travel Time to Home</label>
            <DurationSelector
              value={homeTravelTime}
              onChange={setHomeTravelTime}
            />
          </div>

          <TextArea
            value={notes}
            onChange={handleNotesChange}
            placeholder="Here fill out other notes, like toll price, detours, delays, accidents, etc."
          />
        </div>
      </div>

      <div className={css(styles.submitButtonContainer)}>
        <SubmitButton
          canSubmit={!isSubmitting}
          text="Next"
          onClick={handleSubmit}
        />
      </div>
      <div className={css(styles.verticalSpacer)} />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  content: {
    width: '100%',
    padding: '0 16px',
    marginTop: '20px',
  },
  heading: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: '24px',
  },
  title: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#111827',
  },
  formSection: {
    width: '100%',
  },
  inputGroup: {
    marginBottom: '16px',
    width: '100%',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
    display: 'block',
  },
  verticalSpacer: {
    height: '120px',
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
  },
  dollarSign: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#374151',
    fontSize: '14px',
    pointerEvents: 'none',
    zIndex: 1,
  },
  tollsInput: {
    paddingLeft: '24px !important',
  },
  submitButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  },
})

export default AddExpenses
